/* SansMinds app shell behaviours — installed PWA only.
   Everything here no-ops unless html.pwa-app is set (see theme.liquid head
   script). Ships the interactions the Apple Store app is recognised by:
   a collapsing nav-bar title, a contextual back button, and per-tab scroll
   memory so switching tabs returns you where you left off. */
(function () {
  'use strict';

  var root = document.documentElement;
  if (!root.classList.contains('pwa-app')) return;

  var bar = document.querySelector('[data-pwa-appbar]');
  var tabbar = document.querySelector('[data-pwa-tabbar]');

  function tabRoots() {
    if (!tabbar) return [];
    return Array.prototype.slice.call(tabbar.querySelectorAll('a[href]')).map(function (a) {
      return a.getAttribute('href');
    });
  }

  function normalise(path) {
    if (!path) return '/';
    return path.replace(/\/+$/, '') || '/';
  }

  var here = normalise(location.pathname);
  /* The manifest's start_url is "/", so the storefront home is where a launch
     from the icon lands — treat it as a tab root even though no tab points at
     it, otherwise the app opens showing a back button to nowhere. */
  var isTabRoot = here === '/' || tabRoots().some(function (href) {
    try { return normalise(new URL(href, location.origin).pathname) === here; }
    catch (e) { return false; }
  });

  /* --- Back button ------------------------------------------------------- */
  /* iOS shows a back chevron on pushed screens, never on a tab root. */
  var back = bar && bar.querySelector('[data-pwa-back]');
  if (back) {
    if (!isTabRoot && window.history.length > 1) {
      back.hidden = false;
      back.addEventListener('click', function () { window.history.back(); });
    } else {
      back.hidden = true;
    }
  }

  /* --- Collapsing title -------------------------------------------------- */
  /* The compact title stays hidden until the page's own <h1> passes under the
     bar — Apple's large-title collapse, without restyling anyone's hero. */
  var titleEl = bar && bar.querySelector('[data-pwa-appbar-title]');
  if (titleEl) {
    var h1 = document.querySelector('main h1');
    var label = (h1 && h1.textContent.trim()) || '';
    if (!label) {
      var activeTab = tabbar && tabbar.querySelector('.pwa-tab.is-active .pwa-tab__label');
      label = activeTab ? activeTab.textContent.trim() : (document.title.split('–')[0] || '').trim();
    }
    titleEl.textContent = label;

    if (h1 && 'IntersectionObserver' in window) {
      var barH = bar.offsetHeight || 44;
      new IntersectionObserver(function (entries) {
        bar.classList.toggle('is-titled', !entries[0].isIntersecting);
      }, { rootMargin: '-' + barH + 'px 0px 0px 0px', threshold: 0 }).observe(h1);
    } else {
      /* No h1 to track (the storefront home has none at all), or no
         IntersectionObserver. Showing the title immediately left the bar
         opaque with the browser title sitting in it at rest, which is exactly
         what the reference does not do — so fall back to revealing it once the
         page has actually been scrolled. */
      var onScroll = function () {
        bar.classList.toggle('is-titled', window.scrollY > 24);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }

  /* --- Per-tab scroll memory --------------------------------------------- */
  /* Apple keeps each tab parked where you left it. The browser only restores
     on back/forward, so we remember tab roots ourselves for the session. */
  var KEY = 'pwa:scroll:';
  function save() {
    if (!isTabRoot) return;
    try { sessionStorage.setItem(KEY + here, String(window.scrollY)); } catch (e) {}
  }
  window.addEventListener('pagehide', save);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') save();
  });

  if (isTabRoot) {
    var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    var isReload = nav && nav.type === 'reload';
    if (!isReload) {
      try {
        var y = sessionStorage.getItem(KEY + here);
        if (y !== null && Number(y) > 0) {
          // After layout, so late-loading sections don't fight the restore.
          requestAnimationFrame(function () {
            requestAnimationFrame(function () { window.scrollTo(0, Number(y)); });
          });
        }
      } catch (e) {}
    }
  }

  /* --- The fan ------------------------------------------------------------ */
  /* One trigger holding search, bag, account and appearance. Opening arcs them
     out, dims the page and folds the tab pill away so the arc has room. */
  var fan = document.querySelector('[data-pwa-fan]');
  var dock = document.querySelector('[data-pwa-tabdock]');
  var scrim = null;

  function fanIsOpen() { return !!fan && fan.classList.contains('is-open'); }

  function setFan(open) {
    if (!fan) return;
    fan.classList.toggle('is-open', open);
    if (dock) dock.classList.toggle('is-fanned', open);

    var trigger = fan.querySelector('[data-pwa-fan-toggle]');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (open && !scrim) {
      scrim = document.createElement('div');
      scrim.className = 'pwa-fan__scrim';
      document.body.appendChild(scrim);
      // Force a frame so the opacity transition actually runs.
      requestAnimationFrame(function () { scrim.classList.add('is-open'); });
      scrim.addEventListener('click', function () { setFan(false); });
    } else if (!open && scrim) {
      var dying = scrim;
      scrim = null;
      dying.classList.remove('is-open');
      setTimeout(function () {
        if (dying.parentNode) dying.parentNode.removeChild(dying);
      }, 240);
    }
  }

  if (fan) {
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;

      if (e.target.closest('[data-pwa-fan-toggle]')) {
        e.preventDefault();
        setFan(!fanIsOpen());
        return;
      }
      /* Picking an action folds the fan away. The appearance switch is
         deliberately excluded so modes can be cycled and compared in place. */
      if (fanIsOpen() && e.target.closest('[data-pwa-fan-close]')) {
        setFan(false);
        return;
      }
      /* Anything outside the fan closes it — the scrim covers most of this,
         but the tab pill and the bar sit above the scrim. */
      if (fanIsOpen() && !e.target.closest('[data-pwa-fan]')) setFan(false);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && fanIsOpen()) setFan(false);
    });
  }

  /* --- Dark mode switch ---------------------------------------------------
     A plain on/off control, not a three-way cycle — "System / Light / Dark"
     made people ask what it meant. The system state is still preserved, just
     invisibly: with no stored preference the app follows the phone, and if you
     flip the switch to whatever the phone is already doing the override is
     cleared rather than pinned. So "follow my phone" survives without being a
     third position anyone has to understand. */
  var THEME_KEY = 'sm:theme';

  function systemPrefersDark() {
    return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  }
  function storedMode() {
    var attr = root.getAttribute('data-theme');
    return (attr === 'light' || attr === 'dark') ? attr : null;
  }
  function isDarkNow() {
    var m = storedMode();
    return m ? m === 'dark' : systemPrefersDark();
  }

  /* The two media-scoped theme-color metas follow the OS, so an explicit
     choice needs a media-less one ahead of them or the status bar keeps the
     system's colour while the app shows the opposite. */
  function syncThemeColor(mode) {
    var head = document.head;
    var forced = head.querySelector('meta[name="theme-color"][data-pwa-forced]');
    if (!mode) {
      if (forced && forced.parentNode) forced.parentNode.removeChild(forced);
      return;
    }
    if (!forced) {
      forced = document.createElement('meta');
      forced.setAttribute('name', 'theme-color');
      forced.setAttribute('data-pwa-forced', '');
      head.insertBefore(forced, head.querySelector('meta[name="theme-color"]'));
    }
    forced.setAttribute('content', mode === 'dark' ? '#000000' : '#ffffff');
  }

  function paintSwitches() {
    var on = isDarkNow();
    var mode = storedMode();
    Array.prototype.forEach.call(document.querySelectorAll('[data-pwa-dark]'), function (sw) {
      sw.classList.toggle('is-on', on);
      sw.setAttribute('aria-checked', on ? 'true' : 'false');
      var noteEl = sw.closest('.notif-row');
      noteEl = noteEl && noteEl.querySelector('[data-pwa-dark-note]');
      if (noteEl) {
        noteEl.textContent = sw.getAttribute(
          !mode ? 'data-note-following' : (mode === 'dark' ? 'data-note-on' : 'data-note-off')
        ) || '';
      }
    });
  }

  function setDark(wantDark) {
    /* Asking for exactly what the phone already does means "follow the phone",
       so drop the override instead of freezing the current value. */
    var mode = (wantDark === systemPrefersDark()) ? null : (wantDark ? 'dark' : 'light');
    if (mode) root.setAttribute('data-theme', mode);
    else root.removeAttribute('data-theme');
    try {
      if (mode) localStorage.setItem(THEME_KEY, mode);
      else localStorage.removeItem(THEME_KEY);
    } catch (e) {}
    syncThemeColor(mode);
    paintSwitches();
  }

  paintSwitches();
  syncThemeColor(storedMode());

  document.addEventListener('click', function (e) {
    var sw = e.target.closest && e.target.closest('[data-pwa-dark]');
    if (!sw) return;
    e.preventDefault();
    setDark(!isDarkNow());
  });

  /* While no override is stored the app tracks the phone live. */
  if (window.matchMedia) {
    var mq = window.matchMedia('(prefers-color-scheme: dark)');
    var onSystemChange = function () { if (!storedMode()) paintSwitches(); };
    if (mq.addEventListener) mq.addEventListener('change', onSystemChange);
    else if (mq.addListener) mq.addListener(onSystemChange);
  }

  /* --- Keep the shell's cart badges in sync ------------------------------ */
  /* storefront.js already updates every [data-cart-count-bubble]; the tab-bar
     and nav-bar badges carry that attribute, so they come along for free. */
})();
